/**
 * Columna central: hilo de mensajes con separadores por dia, carga de mensajes
 * antiguos al llegar arriba y aviso de "escribiendo".
 */
import { useMemo } from 'react';
import type { ReactElement, RefObject, UIEvent } from 'react';
import type { MessageView } from '@shared/api-types.ts';
import { formatDayLabel, parseDate, startOfDay } from '../format.ts';
import { EmptyState, ErrorBanner, SkeletonRows } from './Feedback.tsx';
import { MessageBubble } from './MessageBubble.tsx';

type DayGroup = { key: string; label: string; messages: MessageView[] };

function groupByDay(messages: MessageView[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const message of messages) {
    const date = parseDate(message.createdAt);
    const key = date ? startOfDay(date).toISOString() : 'sin-fecha';
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.messages.push(message);
    } else {
      groups.push({ key, label: formatDayLabel(message.createdAt), messages: [message] });
    }
  }
  return groups;
}

export function MessageThread({
  messages,
  contactName,
  loading,
  error,
  onRetry,
  loadingOlder,
  hasOlder,
  onLoadOlder,
  containerRef,
  onScroll,
  typing,
}: {
  messages: MessageView[];
  contactName: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  loadingOlder: boolean;
  hasOlder: boolean;
  onLoadOlder: () => void;
  containerRef: RefObject<HTMLDivElement>;
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  typing: 'bot' | 'agent' | null;
}): ReactElement {
  const groups = useMemo(() => groupByDay(messages), [messages]);

  return (
    <div className="thread">
      <div className="thread__scroll" ref={containerRef} onScroll={onScroll}>
        {error && !loading ? <ErrorBanner message={error} onRetry={onRetry} /> : null}

        {loading ? <SkeletonRows rows={6} /> : null}

        {!loading && !error && messages.length === 0 ? (
          <EmptyState
            icon="inbox"
            title="Todavia no hay mensajes"
            description={`Cuando ${contactName} escriba o envies tu el primer mensaje, aparecera aqui.`}
          />
        ) : null}

        {!loading && !error && messages.length > 0 ? (
          <>
            <div className="thread__start">
              {hasOlder ? (
                <button
                  type="button"
                  className="button button--ghost button--sm"
                  onClick={onLoadOlder}
                  disabled={loadingOlder}
                >
                  {loadingOlder ? 'Cargando mensajes anteriores…' : 'Cargar mensajes anteriores'}
                </button>
              ) : (
                <span className="thread__start-text">Inicio de la conversacion</span>
              )}
            </div>

            {groups.map((group) => (
              <section className="daygroup" key={group.key}>
                <div className="daygroup__separator">
                  <span>{group.label}</span>
                </div>
                {group.messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </section>
            ))}
          </>
        ) : null}

        {typing ? (
          <div className="typing" role="status">
            <span className="typing__dots">
              <span />
              <span />
              <span />
            </span>
            {typing === 'bot' ? 'El asistente de IA esta escribiendo…' : 'Un agente esta escribiendo…'}
          </div>
        ) : null}
      </div>
    </div>
  );
}
